import { QueryClient } from '@tanstack/react-query';

/**
 * P2 FIX: Optimized QueryClient configuration with smart caching
 * 
 * Caching Strategy:
 * - staleTime: How long data is considered "fresh" (won't refetch)
 * - gcTime: How long inactive data stays in cache (garbage collection)
 * - refetchOnWindowFocus: Only refetch when data is stale
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 2 minutes - reduces unnecessary refetches
      staleTime: 2 * 60 * 1000,
      
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      
      // Only refetch on focus if data is stale
      refetchOnWindowFocus: false,
      
      // Retry failed requests 2 times with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Don't refetch on mount if data exists and is fresh
      refetchOnMount: true,
    },
    mutations: {
      // K04 (UX-Audit 26.09.2026): KEINE automatische Wiederholung. Ein
      // Schreibvorgang, der nach einem Timeout oder 5xx wiederholt wird, kann
      // beim ersten Mal schon angekommen sein; set_band, unregister-merchant-
      // centers oder toggle_feature_vote liefen so doppelt. Eine Mutation, die
      // nachweislich idempotent ist, setzt `retry` selbst und begruendet es.
      retry: 0,
    },
  },
});

/**
 * Query key factory for consistent cache keys
 * This ensures cache hits across components
 */
export const queryKeys = {
  // Google accounts
  googleAccounts: {
    all: ['google-accounts'] as const,
    detail: (id: string) => ['google-accounts', id] as const,
  },
  
  // Merchant centers
  merchantCenters: {
    all: ['merchant-centers'] as const,
    byAccount: (accountId: string) => ['merchant-centers', accountId] as const,
    detail: (id: string) => ['merchant-centers', 'detail', id] as const,
  },
  
  // Feeds
  feeds: {
    all: ['feeds'] as const,
    byMerchant: (merchantId: string) => ['feeds', merchantId] as const,
    detail: (id: string) => ['feeds', 'detail', id] as const,
  },
  
  // Products
  products: {
    byFeed: (feedId: string) => ['products', feedId] as const,
    byFeedPaginated: (feedId: string, page: number, filters: object) => 
      ['products', feedId, page, filters] as const,
    stats: (feedId: string) => ['products', feedId, 'stats'] as const,
  },
  
  // Optimization jobs
  optimizationJobs: {
    all: ['optimization-jobs'] as const,
    active: (userId: string) => ['optimization-jobs', 'active', userId] as const,
    byFeed: (feedId: string) => ['optimization-jobs', 'feed', feedId] as const,
    detail: (id: string) => ['optimization-jobs', id] as const,
    history: (userId: string) => ['optimization-jobs', 'history', userId] as const,
  },
  
  // User profile
  profile: {
    current: ['profile'] as const,
  },

  // Feature requests
  featureRequests: {
    all: ['feature-requests'] as const,
    byStatus: (status: string) => ['feature-requests', 'status', status] as const,
    userVotes: (userId: string) => ['feature-request-votes', userId] as const,
  },

  // Feedback
  feedback: {
    all: ['feedback'] as const,
    byUser: (userId: string) => ['feedback', userId] as const,
  },
};

/**
 * Invalidate all product-related queries for a feed
 * Use after product updates, optimizations, etc.
 */
export const invalidateProductQueries = (feedId: string) => {
  queryClient.invalidateQueries({ queryKey: ['products', feedId] });
};

/**
 * Invalidate all queries for a user
 * Use after significant account changes
 */
export const invalidateUserQueries = () => {
  queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
  queryClient.invalidateQueries({ queryKey: ['merchant-centers'] });
  queryClient.invalidateQueries({ queryKey: ['feeds'] });
  queryClient.invalidateQueries({ queryKey: ['optimization-jobs'] });
  queryClient.invalidateQueries({ queryKey: ['google-accounts', 'context-tree'] });
};

